<template>
  <ValidationObserver ref="observer">
    <v-row>
      <v-col class="pb-0">
        <p class="mb-0">
          {{ $t('forms.labels.sendTo') }}
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <VAutocomplete
          v-model="data.selectedUsers"
          :label="$t('forms.labels.selectUser')"
          :items="getUsers"
          :rules="{ required: true }"
          :multiple="true"
          data-cy="cyData"
          class="text-field-transparent"
          itemValue="key"
          itemText="name"
          outlined
          solo
          flat
          :withCustomItemText="true"
        >
          <template
            #customItemText="{ item }"
          >
            <v-row>
              <v-list-item>
                <v-list-item-icon style="align-self: center !important">
                  <v-icon v-if="checkCondition(item.item)">
                    check_box
                  </v-icon>
                  <v-icon v-else>
                    check_box_outline_blank
                  </v-icon>
                </v-list-item-icon>
                <v-row align="center">
                  <v-col
                    cols="auto"
                    class="p-1"
                  >
                    <span>{{ item.item.name }}</span>
                  </v-col>
                  <v-col
                    cols="auto"
                    class="p-1"
                  >
                    <span style="font-style: italic; font-weight: 600">{{ `(${item.item.role ? item.item.role : item.item.jobTitle})` }}</span>
                  </v-col>
                  <v-col
                    cols="auto"
                    class="p-1"
                  >
                    <span style="color: #8D969F">{{ item.item.email }}</span>
                  </v-col>
                </v-row>
              </v-list-item>
            </v-row>
          </template>
          <template #selection="chipData">
            <v-chip
              v-bind="chipData.attrs"
              :inputValue="chipData.selected"
              close
              :color="chipData.item.isMember ? '#c7def2' : ''"
              @click="chipData.select"
              @click:close="removeUser(chipData.item)"
            >
              {{ chipData.item.name }}
            </v-chip>
          </template>
        </VAutocomplete>
      </v-col>
    </v-row>
    <v-row v-if="displayEmailObject">
      <v-col>
        <VTextField
          v-model="data.emailObject"
          outlined
          :label="$t('forms.labels.emailObject')"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col class="pb-0">
        <p class="mb-0">
          {{ $t('forms.labels.addReportLang') }}
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <VAutocomplete
          v-model="data.otherReportLangs"
          :label="$t('forms.labels.selectLanguage')"
          :items="reportOptionalLangs"
          :multiple="true"
          class="text-field-transparent"
          outlined
          solo
          flat
        />
      </v-col>
    </v-row>
  </ValidationObserver>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';

export default {
    components: {
        ValidationObserver,
        VAutocomplete,
        VTextField,
    },
    props: {
        cyData: {
            type: String,
            default: () => 'userToSendReportTo',
        },
        isLab: {
            type: Boolean,
            default: false,
        },
        displayEmailObject: {
            type: Boolean,
            default: false,
        },
        initPayload: {
            type: Object,
            default: () => null,
        },
    },
    data() {
        return {
            data: {
                selectedUsers: [],
                otherReportLangs: [],
                emailObject: '',
                users: [],
            },
        };
    },
    computed: {
        ...mapGetters([
            'site',
            'labWorkOrder',
            'workOrder',
            'teamMainLang',
            'teamID',
            'allTeamLangs',
            'client',
        ]),
        getUsers() {
            return this.data.users.length
                ? this.data.users.map(user => (
                    {
                        key: user.memberId ? user.memberId : user.contactId,
                        isMember: !!user.memberId,
                        name: `${user.firstName} ${user.lastName}`,
                        role: user.role,
                        email: user.email,
                        jobTitle: user.jobTitle && user.jobTitle.length ? user.jobTitle : this.$t('forms.labels.contact'),
                    }))
                : [];
        },
        reportOptionalLangs() {
            const siteLang = this.isLab ? this.labWorkOrder.site.lang : this.workOrder.site.lang;
            if (!this.allTeamLangs.length) return [];
            return this.allTeamLangs.map(({ key }) => {
                if (key !== siteLang) { return key; }
                return null;
            }).filter(Boolean);
        },
    },
    watch: {
        data: {
            handler() {
                const users = this.data.selectedUsers.map(selectedUser => {
                    const find = this.data.users.find(us => (us.memberId ? us.memberId === selectedUser : us.contactId === selectedUser));
                    return {
                        isMember: !!find?.memberId,
                        isContact: !!find?.contactId,
                        id: find?.memberId ? find?.memberId : find?.contactId,
                    };
                });

                this.$emit('sendData', this.displayEmailObject ? { selectedUsers: users, emailObject: this.data.emailObject }
                    : { selectedUsers: users, otherReportLangs: this.data.otherReportLangs });
            },
            deep: true,
        },
    },
    async created() {
        this.reloadTeam(this.teamID);
        if (this.initPayload) {
            this.data = { ...this.data, ...this.initPayload };
        }
        await this.loadData();
    },
    methods: {
        ...mapActions(['reloadTeam']),
        async loadData() {
            const { data } = await this.$rq.getAvailableSiteUsers({ siteId: this.site.id || this.labWorkOrder.site.id || this.workOrder.site.id });
            const serviceReport = this.isLab ? 'receiveLabReport' : 'receiveServiceReport';
            data.allUsers.forEach(user => {
                const key = user.memberId ? user.memberId : user.contactId;
                if (key && user[serviceReport] && !this.data.selectedUsers.includes(key)) {
                    this.data.selectedUsers.push(key);
                }
            });

            const clientID = this.isLab ? this?.labWorkOrder?.client?.id : this?.client?.id;
            const dataAccess = await this.$rq.getSalesGroupLeadersByClient(clientID);

            if (dataAccess.data) {
                dataAccess.data.forEach(groupLeader => {
                    if (!this.data.selectedUsers.includes(groupLeader)) {
                        this.data.selectedUsers.push(groupLeader);
                    }
                });
            }

            this.data.users = data.allUsers;
        },

        removeUser(item) {
            const index = this.data.selectedUsers.indexOf(item.key);
            if (index >= 0) this.data.selectedUsers.splice(index, 1);
        },

        checkCondition(item) {
            // the item that comes from slot
            return this.data?.selectedUsers?.includes(item.key);
        },
    },
};
</script>
